/**
 * Date and time utility functions
 * Centralized date/time formatting and parsing
 */

import { format, parseISO } from 'date-fns';

/**
 * Formats a Date object as YYYY-MM-DD for database storage
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatDateForDB = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Formats a Date object as HH:mm:ss for database storage
 * @param date Date to format
 * @returns Formatted time string
 */
export const formatTimeForDB = (date: Date): string => {
  return format(date, 'HH:mm:ss');
};

/**
 * Formats a Date object for datetime-local input (YYYY-MM-DDTHH:mm)
 * This is critical for timezone handling - converts UTC to local time
 *
 * @param date Date to format
 * @returns Formatted datetime string for input[type="datetime-local"]
 *
 * @example
 * const utcDate = new Date('2024-11-06T15:30:00Z'); // 3:30 PM UTC
 * formatDateTimeLocal(utcDate);
 * // Returns: "2024-11-06T09:30" (if user is in UTC-6 timezone)
 */
export const formatDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats a Date object for display (e.g., "Nov 6, 2024")
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatDateForDisplay = (date: Date): string => {
  return format(date, 'MMM d, yyyy');
};

/**
 * Formats a Date object for display with time (e.g., "Nov 6, 2024 at 9:30 AM")
 * @param date Date to format
 * @returns Formatted datetime string
 */
export const formatDateTimeForDisplay = (date: Date): string => {
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Formats a Date object as time only (e.g., "9:30 AM")
 * @param date Date to format
 * @returns Formatted time string
 */
export const formatTimeForDisplay = (date: Date): string => {
  return format(date, 'h:mm a');
};

/**
 * Parses an ISO date string to Date object
 * @param dateString ISO date string
 * @returns Date object
 */
export const parseISODate = (dateString: string): Date => {
  return parseISO(dateString);
};

/**
 * Converts a UTC timestamp string to local Date
 * Handles multiple timezone formats
 *
 * @param timestamp Timestamp string (with or without timezone)
 * @returns Date object in local timezone
 *
 * @example
 * parseUTCTimestamp('2024-11-06 15:30:00');
 * parseUTCTimestamp('2024-11-06T15:30:00Z');
 * parseUTCTimestamp('2024-11-06T15:30:00+00:00');
 * // All return Date object representing local time
 */
export const parseUTCTimestamp = (timestamp: string): Date => {
  // Check if timestamp already has timezone info (Z or +/-offset)
  const hasTimezone = timestamp.endsWith('Z') ||
                      /[+-]\d{2}:\d{2}$/.test(timestamp) ||
                      /[+-]\d{4}$/.test(timestamp);

  // If no timezone, add 'Z' to treat as UTC
  const utcTimestamp = hasTimezone ? timestamp : `${timestamp}Z`;
  return new Date(utcTimestamp);
};

/**
 * Combines a date and time string into a Date object
 * @param dateStr Date string (YYYY-MM-DD)
 * @param timeStr Time string (HH:mm:ss)
 * @returns Combined Date object
 */
export const combineDateAndTime = (dateStr: string, timeStr: string): Date => {
  return parseISO(`${dateStr}T${timeStr}`);
};

/**
 * Checks if a date is in the past
 * @param date Date to check
 * @returns True if date is in the past
 */
export const isDateInPast = (date: Date): boolean => {
  return date < new Date();
};

/**
 * Checks if a date is today
 * @param date Date to check
 * @returns True if date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Gets the start of day (midnight) for a given date
 * @param date Date to process
 * @returns Date object at start of day
 */
export const getStartOfDay = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Gets the end of day (23:59:59.999) for a given date
 * @param date Date to process
 * @returns Date object at end of day
 */
export const getEndOfDay = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};
